/**
 * Single source of truth for billing service-line balance math, shared by
 * ts-back-end (normalizeServiceLine) and ts-front-end (posting modals preview,
 * payment tables). Semantics (Payments 1.1):
 *
 * - `copay` holds the payer-stated patient responsibility of a FINAL payer
 *   (the field keeps its legacy name to avoid a jsonb migration). It is the
 *   only amount that ever reaches the patient balance — the patient is billed
 *   what the payer said they owe, never the payer's underpayment.
 * - `transfers` holds patient responsibility stated by a NON-final payer,
 *   pending adjudication by the next payer (COB). It is plan-side AR: part of
 *   the plan balance until the downstream payer's posting supersedes it.
 * - The unexplained gap (allowed − paid − PR) stays plan-side (appeal /
 *   write-off material) and never moves to the patient or into transfers.
 * - Write-off settles only the payer's own gap, never the transferred amount.
 *
 * NOTE: this file is intentionally plain CommonJS JavaScript (types live in
 * the adjacent .d.ts). The front-end webpack build has no TypeScript loader
 * for files outside its src/, so runtime-imported ts-common modules must be
 * parseable as plain JS — same reason the ts-common constants files carry no
 * type annotations.
 */

const toNumber = (value) => Number(value !== null && value !== undefined ? value : 0) || 0;
const round2 = (value) => Number(value.toFixed(2));

const calculateServiceLineBalances = (amounts) => {
    const charged = round2(toNumber(amounts.charged));
    const allowed = round2(toNumber(amounts.allowed));
    const paidRefund = round2(toNumber(amounts.paidRefund));
    const transfers = round2(toNumber(amounts.transfers));
    const copay = round2(toNumber(amounts.copay));
    const writeOff = round2(toNumber(amounts.writeOff));
    // Patient-side forgiveness. Kept apart from `writeOff`, which settles only
    // the payer's own gap: the same dollar must never reduce both balances.
    const patientWriteOff = round2(toNumber(amounts.patientWriteOff));

    const effectiveAllowed = allowed > 0 ? allowed : charged;
    const contractualReduction = Math.max(0, charged - effectiveAllowed);
    const writeOffBeyondContractual = Math.max(0, writeOff - contractualReduction);
    const gap = Math.max(0, effectiveAllowed - paidRefund - copay - transfers);

    return {
        charged,
        allowed,
        paidRefund,
        transfers,
        copay,
        writeOff,
        patientWriteOff,
        planBalance: round2(transfers + Math.max(0, gap - writeOffBeyondContractual)),
        // Deliberately NOT clamped: patientBalance + patientWriteOff must stay
        // exactly copay, so the charge rollup can recover the gross payer-stated
        // responsibility. It can go negative when a later payer reversal shrinks
        // the copay under an earlier forgiveness — displays clamp at zero, and
        // the charge-level balance already clamps at zero.
        patientBalance: round2(copay - patientWriteOff),
    };
};

// Payer-side money fields of a transaction — everything except the
// patient-side forgiveness.
const PAYER_MONEY_FIELDS = ['allowed', 'paidRefund', 'transfers', 'copay', 'writeOff'];

// A row of a payment that has been backed out in full stands for no payer:
// the posting is history, its money nets to nothing.
const isStanding = (tx) => Boolean(tx) && !tx.paymentReversedAt;

const isAdjudication = (tx) => (
    tx.type === 'SYSTEM_PAYMENT'
    || tx.type === 'APPLIED_PAYMENT'
    || PAYER_MONEY_FIELDS.some((field) => round2(toNumber(tx[field])) !== 0)
);

/**
 * Whether a payer has spoken for this line: any transaction posted by a payer
 * (or applied against a payer's statement) or carrying payer-side money.
 * A line whose only rows are patient write-offs and their reversals has NOT
 * been adjudicated — its patient responsibility still comes from the charge's
 * own patient amount, not from `copay`. Nor has a line whose only posting was
 * reversed in full: it reads as never posted on.
 */
const hasPayerAdjudication = (transactions) => (transactions || []).some(
    (tx) => isStanding(tx) && isAdjudication(tx),
);

const MONEY_FIELDS = [...PAYER_MONEY_FIELDS, 'patientWriteOff'];

const payerPositionOf = (tx) => Number(tx.payerPosition) || 1;

/**
 * The money a line carries once its payers have posted, read the way the
 * charge's own rollup reads it, so the line and the charge agree:
 *
 * - paid, patient responsibility and write-offs add up across every payer,
 *   reversals subtracting as they go;
 * - transfers count only from the deepest payer that has posted — a later
 *   payer's adjudication supersedes what an earlier one transferred to it;
 * - allowed is the first payer's: a downstream payer's allowed is its
 *   adjudication of what was transferred, not more allowed. It is a
 *   statement, not money, so a payer that posts twice on a line (a
 *   supplemental payment after an appeal) restates it rather than adding to
 *   it — the latest standing posting's word is taken.
 *
 * `payerPosition` is the waterfall position of the payment a row belongs to.
 * Rows of a payment reversed in full (`paymentReversedAt`) stand for no payer.
 */
const isPosting = (tx) => !tx.type || tx.type === 'SYSTEM_PAYMENT';

const timeOf = (tx) => {
    const time = new Date(tx.date).getTime();

    return Number.isNaN(time) ? 0 : time;
};

const aggregateLineTransactions = (transactions) => {
    const rows = (transactions || []).filter(Boolean);
    const totals = Object.fromEntries(MONEY_FIELDS.map((field) => [
        field,
        round2(rows.reduce((sum, tx) => sum + toNumber(tx[field]), 0)),
    ]));

    const byPosition = new Map();

    rows.forEach((tx) => {
        const position = payerPositionOf(tx);
        const entry = byPosition.get(position) || { transfers: 0, adjudicated: false, latestPosting: null };
        const standing = isStanding(tx) && isAdjudication(tx);

        entry.transfers += toNumber(tx.transfers);
        entry.adjudicated = entry.adjudicated || standing;

        // Later by date; on the same date the later row wins, as rows arrive
        // in payment order.
        if (standing && isPosting(tx) && (!entry.latestPosting || timeOf(tx) >= entry.latestPosting.time)) {
            entry.latestPosting = { time: timeOf(tx), allowed: round2(toNumber(tx.allowed)) };
        }

        byPosition.set(position, entry);
    });

    const positions = [...byPosition.entries()]
        .filter(([, entry]) => entry.adjudicated)
        .sort(([a], [b]) => a - b);
    const first = positions.find(([, entry]) => entry.latestPosting && entry.latestPosting.allowed !== 0);
    const latest = positions[positions.length - 1];

    return {
        ...totals,
        allowed: first ? first[1].latestPosting.allowed : 0,
        transfers: latest ? round2(latest[1].transfers) : 0,
    };
};

/**
 * Routes the payer-stated patient responsibility of a posting: to `transfers`
 * when a downstream payer will adjudicate next (COB), to `copay` when the
 * patient is the next responsible party.
 */
const routePatientResponsibility = (patientResponsibility, nextResponsible) => {
    const amount = round2(toNumber(patientResponsibility));

    return nextResponsible.type === 'PAYER'
        ? { copay: 0, transfers: amount }
        : { copay: amount, transfers: 0 };
};

const toPayerId = (value) => (
    value !== null && value !== undefined && String(value) !== '' ? String(value) : null
);

// A payer repeated later in the chain already adjudicated the charge, so only
// its first appearance stays — the same rule the server applies when it
// persists the chain, so client-side previews can never disagree with it.
const dedupeChain = (chain) => {
    const seen = new Set();

    return chain.filter((payer) => {
        const id = toPayerId(payer.payerId);

        if (seen.has(id)) {
            return false;
        }

        seen.add(id);

        return true;
    });
};

/**
 * The charge's payers in payment order. A charge carrying no ordered list —
 * the read model, which stores only mirrors — folds its
 * primary/secondary/tertiary into the same chain, compacted to 1..n so a gap
 * in the middle still names the hop a COB claim goes to.
 */
const getChargePayerChain = (input) => {
    if (Array.isArray(input.payers) && input.payers.length) {
        return dedupeChain(input.payers
            .filter((payer) => payer && toPayerId(payer.payerId))
            .slice()
            .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0)));
    }

    const mirrors = [
        { payerId: input.primaryPayerId, payerName: input.primaryPayerName },
        { payerId: input.secondaryPayerId, payerName: input.secondaryPayerName },
        { payerId: input.tertiaryPayerId, payerName: input.tertiaryPayerName },
    ];

    return dedupeChain(mirrors.filter((payer) => toPayerId(payer.payerId)))
        .map((payer, index) => ({
            position: index + 1,
            payerId: payer.payerId,
            payerName: payer.payerName !== null && payer.payerName !== undefined
                ? payer.payerName
                : undefined,
        }));
};

/**
 * Walks the chain: the payer after the one that paid, or the patient when that
 * payer is last or outside the chain. A self-pay charge has an empty chain, so
 * the patient is responsible from the start. A payer repeated at a later
 * position is skipped — it already adjudicated the charge.
 */
const deriveNextResponsible = (input) => {
    const paymentBy = toPayerId(input.paymentByPayerId);

    if (!paymentBy) {
        return { type: 'PATIENT' };
    }

    const chain = getChargePayerChain(input);
    const paidIndex = chain.findIndex((payer) => toPayerId(payer.payerId) === paymentBy);

    if (paidIndex < 0) {
        return { type: 'PATIENT' };
    }

    for (let index = paidIndex + 1; index < chain.length; index += 1) {
        const candidate = chain[index];

        if (toPayerId(candidate.payerId) !== paymentBy) {
            return {
                type: 'PAYER',
                payerId: Number(candidate.payerId),
                payerName: candidate.payerName !== null && candidate.payerName !== undefined
                    ? candidate.payerName
                    : undefined,
            };
        }
    }

    return { type: 'PATIENT' };
};

module.exports = {
    calculateServiceLineBalances,
    hasPayerAdjudication,
    aggregateLineTransactions,
    routePatientResponsibility,
    getChargePayerChain,
    deriveNextResponsible,
};
